import { action, computed, observable } from "mobx";
import {
  TgRequirements,
  CreateTgRequirement,
  UpdateTgRequirement,
  DeleteTgRequirement,
  TgRequirementFragment
} from "@graphql";

export class TgRequirementSearchStore {
  @observable items: TgRequirementFragment[] = [];
  @observable loading = false;
  @observable costCenterFilter = "";
  @observable professionFilter = "";

  constructor() {
    this.fetch();
  }

  @action
  fetch = async () => {
    this.loading = true;
    try {
      const result = await TgRequirements.func(
        {
          filter: {
            costCenterId: this.costCenterFilter || undefined,
            profession:
              this.professionFilter && this.professionFilter !== "__standard__"
                ? this.professionFilter
                : undefined,
            professionIsNull:
              this.professionFilter === "__standard__" ? true : undefined,
            activeOnly: false
          }
        },
        { fetchPolicy: "network-only" }
      );
      this.items = result.data.tgRequirements;
    } finally {
      this.loading = false;
    }
  };

  @action
  setCostCenterFilter = (value: string) => {
    this.costCenterFilter = value;
    this.fetch();
  };

  @action
  setProfessionFilter = (value: string) => {
    this.professionFilter = value;
    this.fetch();
  };

  @action
  create = async (input: {
    costCenterId: string;
    profession: string | null;
    minRequiredTG: number;
  }) => {
    await CreateTgRequirement.func(
      {
        input: {
          costCenterId: input.costCenterId,
          profession: input.profession,
          minRequiredTG: input.minRequiredTG
        }
      },
      { refetchQueries: [TgRequirements.OperationName] }
    );
    await this.fetch();
  };

  @action
  update = async (id: string, minRequiredTG: number) => {
    await UpdateTgRequirement.func(
      {
        id,
        input: { minRequiredTG }
      },
      { refetchQueries: [TgRequirements.OperationName] }
    );
    await this.fetch();
  };

  @action
  delete = async (id: string) => {
    await DeleteTgRequirement.func(
      { id },
      { refetchQueries: [TgRequirements.OperationName] }
    );
    await this.fetch();
  };

  @computed
  get sortedItems() {
    return [...this.items].sort((a, b) => {
      const ccCompare = a.costCenterId.localeCompare(b.costCenterId);
      if (ccCompare !== 0) return ccCompare;
      if (a.profession === null && b.profession !== null) return -1;
      if (a.profession !== null && b.profession === null) return 1;
      return (a.profession || "").localeCompare(b.profession || "");
    });
  }
}
